import { AsyncPipe } from '@angular/common';
import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { ButtonComponent, InputComponent, NotificationComponent } from '@zero/shared/ui-web-design-system';
import { AuthStateFacade } from '@zero/shared/util-global-state';
import { TrackingService } from '@zero/shared/util-web-app-services';
import { commonRoutes } from 'constants/web-app-constants';
import { BehaviorSubject, combineLatest, filter, firstValueFrom, map, switchMap, tap } from 'rxjs';
import { BaseComponent } from '../../components/base.component';
import { OnboardingContainerComponent } from '../../components/onboarding-container/onboarding-container.component';
import { EmailPipe } from '../../pipes/validation/email/email.pipe';
import { PasswordPipe } from '../../pipes/validation/password/password.pipe';

@Component({
  imports: [
    OnboardingContainerComponent,
    ButtonComponent,
    InputComponent,
    TranslocoPipe,
    RouterLink,
    EmailPipe,
    PasswordPipe,
    AsyncPipe,
    NotificationComponent,
  ],
  templateUrl: './login-after-email-confirmation.component.html',
  styleUrl: './login-after-email-confirmation.component.scss',
})
export class LoginAfterEmailConfirmationComponent extends BaseComponent {
  readonly #route = inject(ActivatedRoute);
  readonly #destroyRef = inject(DestroyRef);
  readonly #authStateFacade = inject(AuthStateFacade);
  readonly #trackingService = inject(TrackingService);

  public readonly passwordForgetLink = commonRoutes.prelogin.forgot_pwd;
  public readonly resendConfirmationLink = commonRoutes.prelogin.resend_confirmation_mail;

  readonly #email = new BehaviorSubject<string>('');
  readonly #password = new BehaviorSubject<string>('');

  public readonly email$ = this.#email.asObservable();
  public readonly password$ = this.#password.asObservable();
  public readonly isFormValid$ = combineLatest([this.email$, this.password$, this.facade.loggingIn$]).pipe(
    map(([email, password, loggingIn]) => !!(new EmailPipe().transform(email) && new PasswordPipe().transform(password) && !loggingIn)),
  );
  public autocomplete = true;
  public isConfirmed: boolean;

  protected override populateFromData(): void {
    this.#route.queryParams
      .pipe(
        map((queryParams) => ({
          email: queryParams['email'],
          code: queryParams['code'],
          adAgreed: queryParams['adAgreed'] !== '' && String(queryParams['adAgreed']).toLowerCase() === 'true',
        })),
        tap(({ email }) => this.#email.next(email)),
        filter(({ email, code }) => !!email && !!code),
        tap(({ email, code, adAgreed }) => this.facade.sendConfirmationData(email, code, adAgreed)),
        switchMap(() => this.facade.sentConfirmationDataSuccessfully$),
        filter((isSuccessful) => isSuccessful),
        tap(() => this.#trackingService.trackEvent('reg_doi', 'register', 'component')),
      )
      .subscribe(() => (this.isConfirmed = true));
  }

  protected override listenToSuccessfulSubmit(): void {
    this.#authStateFacade.loggedIn$
      .pipe(
        filter((loggedIn) => !!loggedIn),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe(() => this.facade.goToStepByRoute(commonRoutes.postlogin.onboarding.redirect));
  }

  protected override async submit(): Promise<void> {
    (await firstValueFrom(this.isFormValid$)) && this.facade.login(this.#email.value, this.#password.value);
  }

  public handleEmailChange = (email: string) => this.#email.next(email);
  public handlePasswordChange = (password: string) => this.#password.next(password);
}
